import { profileMapsOf, wazeHref, type PublicShop } from "@fusiqo/vozqi-shared";
import { Fragment } from "react";
import { getMapsEmbedKey } from "../lib/config";

/**
 * «DÓNDE ESTAMOS», a map to tap and find the place: only for a
 * business that attends in a shop -- the street stays home otherwise. The map
 * is Google's (Maps Embed API), lazy, at the business's pin when placed,
 * and a tap on it opens Google Maps: on a
 * phone an embedded map would catch the finger that scrolls the page. Under
 * it, the street, «Abrir en Google Maps» and «Cómo llegar» in Waze. A build
 * without the key draws the address and the buttons, no map.
 */
export function ProfileLocation({ shop }: { shop: PublicShop }) {
  const maps = profileMapsOf(shop, getMapsEmbedKey());
  const waze = wazeHref(shop.profile);
  // A pin may stand without a street written: then the zone alone, or nothing.
  const lines = [shop.profile.street, shop.profile.zone].filter((line) => line !== null);
  if (maps === null) return null;
  return (
    <section className="profile-card" aria-labelledby="profile-location">
      <h2 id="profile-location" className="profile-card-title">
        Dónde estamos
      </h2>
      {maps.embed === null ? null : (
        <div className="profile-map">
          <iframe
            title={`Mapa: ${shop.business.name}`}
            src={maps.embed}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            // oxlint-disable-next-line react/iframe-missing-sandbox -- the frame is Google's origin, never this one: allow-same-origin keeps it Google's (the map needs that), it never reaches this page; its «Ver mapa más grande» opens Google Maps as an ordinary tab
            sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          />
          {/* The whole map opens Google Maps; the button below is the same link for a keyboard or a reader. */}
          <a className="profile-map-open" href={maps.open} target="_blank" rel="noopener noreferrer" tabIndex={-1} aria-hidden="true" />
        </div>
      )}
      {lines.length === 0 ? null : (
        <address className="profile-address">
          {lines.map((line, index) => (
            <Fragment key={line}>
              {index === 0 ? null : <br />}
              {line}
            </Fragment>
          ))}
        </address>
      )}
      <div className="profile-location-actions">
        <a className="profile-button" href={maps.open} target="_blank" rel="noopener noreferrer">
          Abrir en Google Maps
        </a>
        {waze === null ? null : (
          <a className="profile-button" href={waze} target="_blank" rel="noopener noreferrer">
            Cómo llegar
          </a>
        )}
      </div>
    </section>
  );
}
